import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Button from '../../components/common/Button';
import ConfirmDeleteModal from '../../components/common/ConfirmDeleteModal';
import { workflowsApi, tasksApi, extractOne } from '../../api/facades';

const WorkflowDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const numId = id ? parseInt(id, 10) : NaN;
  const [showDelete, setShowDelete] = React.useState(false);

  const { data: res, isLoading, isError } = useQuery({
    queryKey: ['workflows', numId],
    queryFn: () => workflowsApi.get(numId),
    enabled: !Number.isNaN(numId),
  });
  const workflow = extractOne(res);
  const tasks = (workflow as any)?.tasks ?? [];

  const deleteMu = useMutation({
    mutationFn: () => workflowsApi.remove(numId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      setShowDelete(false);
      navigate('/dashboard/workflows');
      toast.success('Workflow deleted.');
    },
    onError: (e: any) => toast.error(e?.message || 'Delete failed.'),
  });

  if (Number.isNaN(numId) || isError || (!isLoading && !workflow)) {
    return (
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
        <p className="text-slate-600 dark:text-slate-400">Workflow not found.</p>
        <Button variant="secondary" className="mt-4" onClick={() => navigate('/dashboard/workflows')}>Back</Button>
      </div>
    );
  }

  const w = workflow as any;

  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-slate-700 dark:text-slate-200">{isLoading ? 'Loading…' : w?.name ?? 'Workflow'}</h1>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => navigate('/dashboard/workflows')}>Back</Button>
          <Button variant="secondary" onClick={() => navigate(`/dashboard/workflows/${numId}/diagram`)}>View diagram</Button>
          <Button variant="primary" onClick={() => navigate(`/dashboard/workflows/${numId}/edit`)}>Edit</Button>
          <Button variant="secondary" onClick={() => setShowDelete(true)} className="text-red-600 hover:text-red-700">Delete</Button>
        </div>
      </div>
      {isLoading ? (
        <p className="text-slate-500">Loading…</p>
      ) : (
        <>
          {w?.description && <p className="text-slate-600 dark:text-slate-400 mb-4">{w.description}</p>}
          <div className="flex items-center justify-between gap-2 mb-2">
            <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Tasks</h2>
            <Button variant="primary" onClick={() => navigate(`/dashboard/workflows/${numId}/tasks/new`)} className="text-xs">Add task</Button>
          </div>
          {tasks.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">No tasks yet. Add a task to define the workflow steps.</p>
          ) : (
            <ul className="space-y-2">
              {tasks.map((t: any) => (
                <li key={t.id} className="flex items-center justify-between gap-2 border border-slate-200 dark:border-slate-700 rounded-lg p-3">
                  <div>
                    <span className="font-medium text-slate-800 dark:text-slate-100">{t.task_subject}</span>
                    <span className="text-xs text-slate-500 ml-2">Order: {t.order ?? '—'}</span>
                    {t.depends_on_task_id && <span className="text-xs text-slate-500 ml-2">Depends on task #{t.depends_on_task_id}</span>}
                    <div className="text-xs text-slate-500 mt-1">
                      Status: {t.project_status ?? '—'} · Priority: {t.priority ?? '—'}
                      {t.members?.length > 0 && ` · Assignees: ${t.members.map((m: any) => m.user?.people?.full_name ?? m.user?.email ?? m.user_id).join(', ')}`}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="secondary" className="text-xs" onClick={() => navigate(`/dashboard/tasks/${t.id}/edit`)}>Edit</Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      <ConfirmDeleteModal open={showDelete} onClose={() => setShowDelete(false)} onConfirm={() => deleteMu.mutate()} title="Delete workflow" message="Are you sure? All tasks in this workflow will be deleted." loading={deleteMu.isPending} />
    </div>
  );
};

export default WorkflowDetailPage;
